import { useMemo, useState } from 'react';
import { AlarmClock, CalendarCheck2, CalendarRange, CheckCircle2, Plus, Settings2, Sparkles, Wand2 } from 'lucide-react';
import { pedDeadline, PLATFORM_LABELS, type ContentItem, type EditorialConfig, type Workspace } from '@unitly/contracts';
import { useContentItems, useEditorialConfig, useEditorialSettings, useGenerateMonth, useUpdateContent } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { MonthGrid, MonthNav, chipColor, type GridItem } from '../../ui/MonthGrid.tsx';
import { EmptyState, PageLoader, Segmented } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';
import { monthLabel, monthOf, monthViewRange } from '../../lib/calendar.ts';
import { diffDays, longDate } from '../../lib/dates.ts';
import { Agenda } from '../calendar/Agenda.tsx';
import { PedSettingsSheet } from './PedSettings.tsx';

type View = 'calendar' | 'list' | 'board';
const readView = (): View => {
  try { const v = localStorage.getItem('unitly-ped-view'); return v === 'list' || v === 'board' ? v : 'calendar'; } catch { return 'calendar'; }
};

function itemSub(item: ContentItem, cfg: EditorialConfig | undefined) {
  const parts: React.ReactNode[] = [];
  const fields = cfg?.cardFields ?? ['series', 'format', 'channels'];
  parts.push(<span key="st" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span className="state-dot" style={{ '--sd': chipColor(item.state.color) } as React.CSSProperties} />{item.state.name}</span>);
  if (fields.includes('series') && item.series) parts.push(<span key="se">{item.series.name}</span>);
  if (fields.includes('format') && item.format) parts.push(<span key="fo">{item.format}</span>);
  if (fields.includes('channels') && item.channels.length) parts.push(<span key="ch">{item.channels.map((c) => PLATFORM_LABELS[c.platform]).join(' · ')}</span>);
  if (fields.includes('owner')) { const o = item.members.find((m) => m.role === 'owner'); if (o) parts.push(<span key="ow">{o.displayName}</span>); }
  if (item.approval.status === 'approved') parts.push(<span key="ap" style={{ color: 'var(--green)', display: 'inline-flex', alignItems: 'center', gap: 3 }}><CheckCircle2 size={13} /> Revisionato</span>);
  return parts;
}

export function PedView({ workspace }: { workspace: Workspace }) {
  const { orgId, today, openContent, openQuickAdd } = useOrgCtx();
  const config = useEditorialConfig(orgId, workspace.id);
  const [month, setMonth] = useState(() => monthOf(today));
  const [view, setViewState] = useState<View>(readView);
  const [selected, setSelected] = useState<string | null>(today);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const setView = (v: View) => { setViewState(v); try { localStorage.setItem('unitly-ped-view', v); } catch { /* ignore */ } };
  const range = monthViewRange(month);
  const items = useContentItems(orgId, { workspaceId: workspace.id, from: range.from, to: range.to, includeUnscheduled: view === 'board' ? '1' : undefined }, Boolean(config.data?.enabled));
  const update = useUpdateContent(orgId);
  const generate = useGenerateMonth(orgId, workspace.id);
  const toast = useToast();

  if (config.isLoading) return <PageLoader />;
  const cfg = config.data;
  if (!cfg?.enabled) return <EnablePed workspace={workspace} canEnable={Boolean(cfg?.permissions.admin)} />;

  const list = items.data ?? [];
  const inMonth = list.filter((i) => i.plannedDate && monthOf(i.plannedDate) === month);
  const gridItems: GridItem[] = list.filter((i) => i.plannedDate).map((i) => ({
    id: i.id, date: i.plannedDate!, title: i.title, time: i.plannedTime, color: i.series?.color ?? i.state.color ?? workspace.color,
    done: i.state.category === 'done', draggable: i.permissions.edit,
  }));
  const hasSchedule = cfg.series.some((s) => s.weekdays.length > 0);

  return (
    <>
      <div className="toolbar" style={{ gap: 6 }}>
        <MonthNav month={month} onChange={(m) => { setMonth(m); setSelected(m === monthOf(today) ? today : `${m}-01`); }} today={today} />
        <span className="spacer" />
        <Segmented label="Vista PED" value={view} onChange={setView} options={[{ value: 'calendar', label: 'Calendario' }, { value: 'list', label: 'Lista' }, { value: 'board', label: 'Board' }]} />
      </div>

      <DeadlineBanner month={month} cfg={cfg} items={inMonth} today={today} />

      <div className="toolbar">
        {cfg.permissions.create && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => openQuickAdd({ kind: 'content', workspaceId: workspace.id, dueDate: selected ?? undefined })}>
            <Plus size={16} /> Contenuto
          </button>
        )}
        {cfg.permissions.create && hasSchedule && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={generate.isPending}
            onClick={() => generate.mutate(month, {
              onSuccess: (r) => toast(r.created ? `${r.created} contenuti creati dal palinsesto per ${monthLabel(month).toLowerCase()}` : 'Il PED del mese ha già tutte le uscite del palinsesto'),
              onError: (e) => toast(e instanceof ApiError ? e.message : 'Generazione non riuscita'),
            })}>
            <Wand2 size={16} /> Genera {monthLabel(month).split(' ')[0]!.toLowerCase()} dal palinsesto
          </button>
        )}
        <span className="spacer" />
        {cfg.permissions.admin && (
          <button type="button" className="btn btn-plain btn-sm" onClick={() => setSettingsOpen(true)}><Settings2 size={16} /> Personalizza</button>
        )}
      </div>

      {cfg.permissions.admin && !cfg.series.length && !list.length && (
        <div className="card card-pad" style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <Sparkles size={22} color="var(--accent)" />
          <span style={{ flex: 1, fontSize: 14.5 }}>
            <strong>Imposta il palinsesto</strong> — definisci le rubriche e i giorni di uscita (es. “What If” martedì e venerdì): Unitly creerà il PED di ogni mese con un clic.
          </span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSettingsOpen(true)}>Imposta</button>
        </div>
      )}

      {view === 'calendar' && (
        <>
          <MonthGrid
            month={month} today={today} selected={selected} onSelect={setSelected} items={gridItems}
            onItemClick={(it) => openContent(it.id)}
            onAdd={cfg.permissions.create ? (date) => openQuickAdd({ kind: 'content', workspaceId: workspace.id, dueDate: date }) : undefined}
            onDropItem={(id, date) => update.mutate({ id, patch: { plannedDate: date } }, { onError: (e) => toast(e instanceof ApiError ? e.message : 'Spostamento non riuscito') })}
          />
          <div className="mobile-day-agenda">
            {selected && (
              <Agenda
                today={today}
                emptyText={`Nessun contenuto ${diffDays(selected, today) === 0 ? 'oggi' : `il ${longDate(selected).toLowerCase()}`}.`}
                entries={list.filter((i) => i.plannedDate === selected).map((i) => ({
                  id: i.id, date: i.plannedDate!, time: i.plannedTime, title: i.title, color: i.series?.color ?? workspace.color,
                  done: i.state.category === 'done', sub: itemSub(i, cfg), onOpen: () => openContent(i.id),
                }))}
                onAddDay={cfg.permissions.create ? (d) => openQuickAdd({ kind: 'content', workspaceId: workspace.id, dueDate: d }) : undefined}
              />
            )}
          </div>
        </>
      )}

      {view === 'list' && (
        inMonth.length ? (
          <Agenda
            today={today}
            entries={inMonth.map((i) => ({
              id: i.id, date: i.plannedDate!, time: i.plannedTime, title: i.title, color: i.series?.color ?? workspace.color,
              done: i.state.category === 'done', sub: itemSub(i, cfg), onOpen: () => openContent(i.id),
            }))}
          />
        ) : (
          <div className="card"><EmptyState tone="neutral" icon={<CalendarRange size={20} />} title={`Nessun contenuto a ${monthLabel(month).toLowerCase()}`}>
            {hasSchedule ? 'Usa “Genera dal palinsesto” per creare le uscite del mese.' : 'Aggiungi un contenuto o imposta il palinsesto delle rubriche.'}
          </EmptyState></div>
        )
      )}

      {view === 'board' && <PedBoard cfg={cfg} items={list.filter((i) => !i.plannedDate || monthOf(i.plannedDate) === month)} />}

      <PedSettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} workspace={workspace} config={cfg} />
    </>
  );
}

function DeadlineBanner({ month, cfg, items, today }: { month: string; cfg: EditorialConfig; items: ContentItem[]; today: string }) {
  const deadline = pedDeadline(month, cfg.deadlineRule);
  if (!deadline && !items.length) return null;
  const toReview = items.filter((i) => i.approval.status !== 'approved' && !['done', 'canceled'].includes(i.state.category)).length;
  const noCaption = items.filter((i) => !i.caption.trim() && !['done', 'canceled'].includes(i.state.category)).length;
  const left = deadline ? diffDays(deadline, today) : null;
  const complete = items.length > 0 && toReview === 0;
  const tone = complete ? 'ok' : left !== null && left < 0 && monthOf(today) <= month ? 'late' : '';
  const monthName = monthLabel(month).split(' ')[0]!.toLowerCase();
  return (
    <div className={`ped-banner ${tone}`} role="status">
      <span className="icon">{complete ? <CalendarCheck2 size={18} /> : <AlarmClock size={18} />}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong>PED di {monthName}</strong>
        {deadline && (monthOf(today) <= month || !complete) && (
          <> · {left! > 0 ? `da chiudere entro ${longDate(deadline).toLowerCase()} (${left} giorni)` : left === 0 ? 'da chiudere oggi' : `scadenza passata (${longDate(deadline).toLowerCase()})`}</>
        )}
        <div className="sub">
          {items.length} contenuti{items.length ? ` · ${toReview} da revisionare · ${noCaption} senza caption` : ''}
        </div>
      </span>
    </div>
  );
}

function PedBoard({ cfg, items }: { cfg: EditorialConfig; items: ContentItem[] }) {
  const { orgId, openContent, today } = useOrgCtx();
  const update = useUpdateContent(orgId);
  const toast = useToast();
  const [over, setOver] = useState<string | null>(null);
  const byState = useMemo(() => new Map(cfg.states.map((s) => [s.id, items.filter((i) => i.state.id === s.id)])), [cfg.states, items]);
  return (
    <div className="board" role="list" aria-label="Board per stato">
      {cfg.states.map((s) => (
        <div key={s.id} role="listitem" className={`board-col${over === s.id ? ' over' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setOver(s.id); }} onDragLeave={() => setOver((o) => (o === s.id ? null : o))}
          onDrop={(e) => {
            e.preventDefault(); setOver(null);
            const id = e.dataTransfer.getData('text/grid-item');
            const item = items.find((i) => i.id === id);
            if (item && item.state.id !== s.id) update.mutate({ id, patch: { stateId: s.id } }, { onError: (err) => toast(err instanceof ApiError ? err.message : 'Spostamento non riuscito') });
          }}>
          <div className="board-col-head">
            <span className="state-pill" style={{ '--sd': chipColor(s.color) } as React.CSSProperties}><span className="state-dot" />{s.name}</span>
            <span className="n">{byState.get(s.id)?.length ?? 0}</span>
          </div>
          {byState.get(s.id)?.map((i) => (
            <button key={i.id} type="button" className="board-card" draggable={i.permissions.edit}
              onDragStart={(e) => e.dataTransfer.setData('text/grid-item', i.id)} onClick={() => openContent(i.id)}
              style={{ boxShadow: `inset 3px 0 0 ${chipColor(i.series?.color)}, var(--shadow-1), 0 0 0 0.5px var(--separator)` }}>
              <span className="task-title">{i.title}</span>
              <span className="task-meta">
                {i.plannedDate && <span className="meta-item">{diffDays(i.plannedDate, today) === 0 ? 'Oggi' : longDate(i.plannedDate)}{i.plannedTime ? ` · ${i.plannedTime}` : ''}</span>}
                {i.format && <span className="meta-item">{i.format}</span>}
                {i.approval.status === 'approved' && <span className="meta-item" style={{ color: 'var(--green)' }}><CheckCircle2 size={13} /> Revisionato</span>}
              </span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

function EnablePed({ workspace, canEnable }: { workspace: Workspace; canEnable: boolean }) {
  const { orgId } = useOrgCtx();
  const enable = useEditorialSettings(orgId, workspace.id);
  const toast = useToast();
  return (
    <div className="card card-pad" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 28 }}>
      <span className="empty" style={{ padding: 0 }}><span className="icon" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}><CalendarRange size={22} /></span></span>
      <strong style={{ fontSize: 18 }}>Calendario di {workspace.name}</strong>
      <p style={{ margin: 0, color: 'var(--label-2)', maxWidth: '46ch', fontSize: 15 }}>
        Calendario dei contenuti con rubriche, formati, canali, caption, link Canva e revisione del PM. Stati e campi sono personalizzabili.
      </p>
      {canEnable ? (
        <button type="button" className="btn btn-primary" disabled={enable.isPending}
          onClick={() => enable.mutate({ path: '/enable', method: 'POST' }, { onSuccess: () => toast('Calendario attivato'), onError: (e) => toast(e.message) })}>
          Attiva il calendario
        </button>
      ) : (
        <p className="hint">Chiedi a chi gestisce lo spazio di attivarlo.</p>
      )}
    </div>
  );
}

